/**
 * notificationMeta.js — saf (React'tan bağımsız test edilebilir) mapping modülü.
 *
 * KRİTİK BULGU (proje-yoneticisi/ui-ux tespiti): backend'de `tone` alanı şu an
 * TÜM gerçek çağrılarda `None` (scheduler.py, interview_simulation.py,
 * notifications.py hepsi tone=None gönderiyor) ve `icon_key="interview"` üç
 * farklı bildirim tipi (interview_available/submitted/scored) için ortak
 * kullanılıyor. Bu yüzden öncelik sırası:
 *   1) `type` alanına dayalı override (en spesifik — interview_* ayrımını yapar)
 *   2) `icon_key` alanına dayalı genel eşleme (backend ileride tone/icon_key'i
 *      daha zengin doldurursa kod değişmeden çalışmaya devam eder)
 *   3) Varsayılan (Info / neutral)
 *
 * Backend ileride `tone` alanını gerçekten doldurmaya başlarsa (VALID_TONES
 * içindeki bir değer), o zaman fallback tablosu devre dışı kalır — resolveTone
 * öncelik olarak backend değerini kullanır.
 */
import {
  BookOpen,
  PlayCircle,
  ClipboardCheck,
  MessageCircle,
  FileText,
  Megaphone,
  Info,
} from 'lucide-react';

// ── İkon adı → component eşlemesi ──────────────────────────────────────────────
const ICON_COMPONENTS = {
  BookOpen,
  PlayCircle,
  ClipboardCheck,
  MessageCircle,
  FileText,
  Megaphone,
  Info,
};

// ── type → ikon adı (en spesifik, önce kontrol edilir) ─────────────────────────
const TYPE_ICON_OVERRIDES = {
  study_inactivity: 'BookOpen',
  interview_available: 'PlayCircle',
  interview_submitted: 'ClipboardCheck',
  interview_scored: 'MessageCircle',
  cv_approved: 'FileText',
  cv_changes_requested: 'FileText',
  announcement: 'Megaphone',
};

// ── icon_key → ikon adı (backend'in gönderdiği genel kategori) ────────────────
const ICON_KEY_MAP = {
  reminder: 'BookOpen',
  interview: 'MessageCircle',
  announcement: 'Megaphone',
  cv: 'FileText',
};

const DEFAULT_ICON_NAME = 'Info';

// ── type → tone fallback (backend tone=None olduğunda kullanılır) ─────────────
const TYPE_TONE_FALLBACK = {
  study_inactivity: 'warning',
  interview_available: 'info',
  interview_submitted: 'neutral',
  interview_scored: 'success',
  cv_approved: 'success',
  cv_changes_requested: 'warning',
  announcement: 'info',
};

const VALID_TONES = ['info', 'success', 'warning', 'danger', 'neutral'];
const DEFAULT_TONE = 'neutral';

/**
 * Bildirimin tonunu çözer.
 * Öncelik: backend'in geçerli bir tone değeri > type fallback tablosu > neutral.
 */
export function resolveTone(notif) {
  if (!notif) return DEFAULT_TONE;
  if (VALID_TONES.includes(notif.tone)) return notif.tone;
  return TYPE_TONE_FALLBACK[notif.type] || DEFAULT_TONE;
}

/**
 * Bildirimin ikon adını çözer (string — test edilebilirlik için ayrı export).
 * Öncelik: type override > icon_key eşlemesi > default.
 */
export function resolveIconName(notif) {
  if (!notif) return DEFAULT_ICON_NAME;
  if (notif.type && TYPE_ICON_OVERRIDES[notif.type]) {
    return TYPE_ICON_OVERRIDES[notif.type];
  }
  if (notif.icon_key && ICON_KEY_MAP[notif.icon_key]) {
    return ICON_KEY_MAP[notif.icon_key];
  }
  return DEFAULT_ICON_NAME;
}

/**
 * Bildirimin ikon component'ini (lucide-react) döner — render'da kullanılır.
 */
export function resolveIcon(notif) {
  const name = resolveIconName(notif);
  return ICON_COMPONENTS[name] || ICON_COMPONENTS[DEFAULT_ICON_NAME];
}

// ── tone → tailwind class seti ─────────────────────────────────────────────────
// chipBg/chipText: unread durumda ikon chip rengi (spec tablosu).
// unreadBg/border: satır zemin/sol şerit rengi.
export const TONE_STYLES = {
  info: {
    chipBg: 'bg-sky-100',
    chipText: 'text-sky-600',
    unreadBg: 'bg-sky-50/70',
    border: 'border-sky-500',
  },
  success: {
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-600',
    unreadBg: 'bg-emerald-50/70',
    border: 'border-emerald-500',
  },
  warning: {
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-600',
    unreadBg: 'bg-amber-50/70',
    border: 'border-amber-500',
  },
  danger: {
    chipBg: 'bg-rose-100',
    chipText: 'text-rose-600',
    unreadBg: 'bg-rose-50/70',
    border: 'border-rose-500',
  },
  neutral: {
    chipBg: 'bg-gray-100',
    chipText: 'text-slate-600',
    unreadBg: 'bg-gray-50/70',
    border: 'border-slate-500',
  },
};

export function resolveToneStyles(notif) {
  const tone = resolveTone(notif);
  return TONE_STYLES[tone] || TONE_STYLES[DEFAULT_TONE];
}
